import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  OneSignalError,
  getAppUrl,
  getPermissionStatus,
  isOneSignalAvailable,
  requestNotificationPermission,
} from "@lyder-talk/core";

import { useProfile } from "./use-profile";
import { auth } from "@lyder-talk/core/lib/auth-client";
import { pathFromUrl, storage } from "@lyder-talk/core/lib/storage-client";
import { getAvatarPathFromUrl } from "@lyder-talk/core/lib/avatar-upload";

const AVATAR_BUCKET = "avatars";
const AVATAR_PUBLIC_PREFIX = "/storage/v1/object/public/avatars/";
const MAX_AVATAR_BYTES = 5 * 1024 * 1024;
const ACCEPTED_AVATAR_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];

const MIN_PASSWORD_LENGTH = 6;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Every side effect behind the profile modal, in one hook.
 *
 * The modal itself was 784 lines because ~500 of them were these handlers —
 * six `useState`s for the email change alone, a FileReader branch, two
 * Supabase round-trips guarded by a re-authentication, and a OneSignal poll.
 * All of it is state and behaviour with no rendering in it, so it lives here
 * and `ProfileModal` is left as layout.
 *
 * ## Toasts are fired here, not at the call sites
 *
 * Every one of these handlers reports through the same toast. Returning a
 * boolean as well means a caller could branch on success, but today none do —
 * so the hook owns the message and the section components stay presentational.
 *
 * ## Close resets the form
 *
 * Leaving a half-typed display name in local state means it reappears the next
 * time the modal opens, which reads as "my save didn't work". Closing re-seeds
 * every field from the saved profile, so what you see on reopen is what is
 * actually stored.
 */
export function useProfileSettings(open: boolean, userId: string | null) {
  const { toast } = useToast();
  const { profile, updateProfile, isUpdating } = useProfile();

  // Profile fields. Seeded from `profile` and re-seeded whenever the modal
  // closes, so an abandoned edit never becomes the next session's default.
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Account / session
  const [email, setEmail] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);

  // Notifications
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission | null>(null);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);

  const seedFromProfile = useCallback(() => {
    setDisplayName(profile?.display_name || "");
    setBio(profile?.bio || "");
    setAvatarUrl(profile?.avatar_url || "");
    setPreviewUrl(profile?.avatar_url || null);
  }, [profile]);

  useEffect(() => {
    if (profile) seedFromProfile();
  }, [profile, seedFromProfile]);

  // Email lives on the auth session, not on `profiles` — it is read when the
  // modal opens rather than fetched eagerly, so a signed-out visit of the
  // settings surface costs nothing.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      if (session?.user?.email) setEmail(session.user.email);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // A closed modal is a discarded draft: drop the credential fields outright
  // (they should never outlive the dialog) and re-seed the profile fields.
  useEffect(() => {
    if (open) return;
    setIsChangingPassword(false);
    setIsChangingEmail(false);
    seedFromProfile();
  }, [open, seedFromProfile]);

  // OneSignal subscribes asynchronously, so a first read can say "default" for
  // a user who has actually already granted. Poll briefly until the push
  // subscription resolves, then stop.
  useEffect(() => {
    if (!open) return;

    const read = () => {
      try {
        const status = getPermissionStatus();
        if (status.browser === "granted" && status.oneSignal) {
          setNotificationPermission("granted");
        } else if (status.browser === "denied") {
          setNotificationPermission("denied");
        } else {
          setNotificationPermission("default");
        }
      } catch (error) {
        console.error("Error checking notification permission:", error);
        if ("Notification" in window) setNotificationPermission(Notification.permission);
      }
    };

    read();

    const interval = setInterval(() => {
      if (isOneSignalAvailable() && window.OneSignal?.User?.PushSubscription) {
        read();
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [open]);

  const requestPermission = useCallback(async () => {
    setIsRequestingPermission(true);
    try {
      const result = await requestNotificationPermission();
      const status = getPermissionStatus();

      if (result.success) {
        toast({
          title: "Notifications Enabled",
          description: "You will now receive push notifications for session shares.",
        });
        setNotificationPermission(
          status.browser === "granted" && status.oneSignal ? "granted" : "default",
        );
        return;
      }

      // Each rejection code maps to a different next step — "you denied it in
      // the browser" and "the service is down" are not the same instruction —
      // so the copy is chosen per code rather than flattened.
      let title = "Permission Denied";
      let description =
        "Please enable notifications in your browser settings to receive push notifications.";

      switch (result.error?.code) {
        case "BROWSER_PERMISSION_DENIED":
          description =
            "Browser notification permission was denied. Please enable it in your browser settings.";
          break;
        case "PERMISSION_DENIED":
          description =
            "Notification permission was denied. Please enable it in your browser settings.";
          break;
        case "PERMISSION_NOT_GRANTED":
          title = "Permission Not Granted";
          description = "Notification permission was not granted. Please try again.";
          break;
        case "NOT_AUTHENTICATED":
          title = "Sign In Required";
          description = "You must be signed in to enable notifications.";
          break;
        case "NOT_AVAILABLE":
        case "SLIDEDOWN_NOT_AVAILABLE":
        case "SUBSCRIPTION_NOT_AVAILABLE":
          title = "Service Unavailable";
          description =
            "Notification service is not available. Please refresh the page and try again.";
          break;
        default:
          title = "Error";
          description =
            result.error?.message || "Failed to request notification permission. Please try again.";
      }

      toast({ title, description, variant: "destructive" });

      if (status.browser === "denied") {
        setNotificationPermission("denied");
      } else if (window.OneSignal?.User?.PushSubscription) {
        setNotificationPermission(
          window.OneSignal.User.PushSubscription.optedIn ? "granted" : "default",
        );
      } else if ("Notification" in window) {
        setNotificationPermission(Notification.permission);
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      toast({
        title: "Error",
        // `OneSignalError.message` is already user-facing copy — flattening it
        // into a generic string is what loses the actionable part.
        description:
          error instanceof OneSignalError
            ? error.message
            : "Failed to request notification permission. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRequestingPermission(false);
    }
  }, [toast]);

  const saveProfile = useCallback(async () => {
    try {
      await updateProfile({
        display_name: displayName || null,
        bio: bio || null,
        avatar_url: avatarUrl || null,
      });
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update profile",
        variant: "destructive",
      });
    }
  }, [avatarUrl, bio, displayName, toast, updateProfile]);

  const uploadAvatar = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!ACCEPTED_AVATAR_TYPES.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a JPEG, PNG, GIF, or WebP image",
          variant: "destructive",
        });
        event.target.value = "";
        return;
      }

      if (file.size > MAX_AVATAR_BYTES) {
        toast({
          title: "File too large",
          description: "Please upload an image smaller than 5MB",
          variant: "destructive",
        });
        event.target.value = "";
        return;
      }

      if (!userId) {
        toast({
          title: "Error",
          description: "You must be logged in to upload images",
          variant: "destructive",
        });
        event.target.value = "";
        return;
      }

      setUploading(true);
      // Preview the local file before the storage round trip — a Data URL
      // rather than `URL.createObjectURL`, because an object URL has to be
      // revoked and the profile effect that swaps in the stored URL only runs
      // after this resolves: revoking here would blank the avatar in between.
      const localPreview = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      }).catch(() => null);

      try {
        // The server names the object from the verified token, so there is no
        // client-built path — which is what stops one user writing into another
        // user's namespace.
        if (avatarUrl.includes("/v1/storage/public/avatars")) {
          const oldPath = pathFromUrl(avatarUrl);
          if (oldPath) await storage.from(AVATAR_BUCKET).remove([oldPath]);
        }

        const { data: uploaded, error: uploadError } = await storage
          .from(AVATAR_BUCKET)
          .upload(file, { contentType: file.type });
        if (uploadError) throw uploadError;
        if (!uploaded) throw new Error("Upload failed");

        const publicUrl = uploaded.url;

        setAvatarUrl(publicUrl);
        setPreviewUrl(localPreview);

        await updateProfile({
          display_name: displayName || null,
          bio: bio || null,
          avatar_url: publicUrl,
        });

        toast({
          title: "Avatar Uploaded",
          description: "Your profile picture has been updated successfully.",
        });
      } catch (error: any) {
        console.error("Error uploading image:", error);
        toast({
          title: "Upload Failed",
          description: error.message || "Failed to upload image. Please try again.",
          variant: "destructive",
        });
        setPreviewUrl(avatarUrl || null);
      } finally {
        setUploading(false);
        event.target.value = "";
      }
    },
    [avatarUrl, bio, displayName, toast, updateProfile, userId],
  );

  const removeAvatar = useCallback(async () => {
    if (!userId) return;
    try {
      // Recover the key from the URL: new URLs carry `?path=`, and legacy
      // Supabase URLs still parse via the bucket segment.
      const existingPath = getAvatarPathFromUrl(avatarUrl);
      if (existingPath) await storage.from(AVATAR_BUCKET).remove([existingPath]);

      setAvatarUrl("");
      setPreviewUrl(null);
      await updateProfile({
        display_name: displayName || null,
        bio: bio || null,
        avatar_url: null,
      });

      toast({
        title: "Avatar Removed",
        description: "Your profile picture has been removed.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to remove image",
        variant: "destructive",
      });
    }
  }, [avatarUrl, bio, displayName, toast, updateProfile, userId]);

  /**
   * Both credential changes re-authenticate before writing.
   *
   * `auth.updateUser` takes effect on the *current* session, so
   * without the `signInWithPassword` check first a learner who borrowed
   * someone's device — or who walked up to an unlocked machine — could change
   * the password or the email on the account without ever knowing the old one.
   * That check is the only thing standing between a session and a takeover, so
   * it stays, and its failure is reported in the learner's own terms rather
   * than surfacing Supabase's message.
   *
   * Both return whether the change landed. The caller uses it to decide
   * whether to clear its draft fields: a rejected change must leave what was
   * typed in place so one field can be corrected, not the whole form retyped.
   */
  const changePassword = useCallback(
    async (
      currentPassword: string,
      newPassword: string,
      confirmPassword: string,
    ): Promise<boolean> => {
      if (!currentPassword || !newPassword || !confirmPassword) {
        toast({
          title: "Error",
          description: "Please fill in all password fields",
          variant: "destructive",
        });
        return false;
      }
      if (newPassword !== confirmPassword) {
        toast({ title: "Error", description: "New passwords do not match", variant: "destructive" });
        return false;
      }
      if (newPassword.length < MIN_PASSWORD_LENGTH) {
        toast({
          title: "Error",
          description: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
          variant: "destructive",
        });
        return false;
      }

      setIsChangingPassword(true);
      try {
        const {
          data: { session },
        } = await auth.getSession();
        if (!session?.user?.email) throw new Error("Not authenticated");

        const { error: signInError } = await auth.signInWithPassword({
          email: session.user.email,
          password: currentPassword,
        });
        if (signInError) throw new Error("Current password is incorrect");

        const { error: updateError } = await auth.updateUser({ password: newPassword });
        if (updateError) throw updateError;

        toast({
          title: "Password Updated",
          description: "Your password has been changed successfully.",
        });
        return true;
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message || "Failed to change password",
          variant: "destructive",
        });
        return false;
      } finally {
        setIsChangingPassword(false);
      }
    },
    [toast],
  );

  const changeEmail = useCallback(
    async (
      newEmail: string,
      confirmEmail: string,
      emailChangePassword: string,
    ): Promise<boolean> => {
      if (!newEmail || !confirmEmail || !emailChangePassword) {
        toast({ title: "Error", description: "Please fill in all fields", variant: "destructive" });
        return false;
      }
      if (newEmail !== confirmEmail) {
        toast({
          title: "Error",
          description: "New email addresses do not match",
          variant: "destructive",
        });
        return false;
      }
      if (!EMAIL_PATTERN.test(newEmail)) {
        toast({
          title: "Error",
          description: "Please enter a valid email address",
          variant: "destructive",
        });
        return false;
      }
      if (newEmail === email) {
        toast({
          title: "Error",
          description: "New email must be different from your current email",
          variant: "destructive",
        });
        return false;
      }

      setIsChangingEmail(true);
      try {
        const {
          data: { session },
        } = await auth.getSession();
        if (!session?.user?.email) throw new Error("Not authenticated");

        const { error: signInError } = await auth.signInWithPassword({
          email: session.user.email,
          password: emailChangePassword,
        });
        if (signInError) throw new Error("Password is incorrect");

        const { error: updateError } = await auth.updateUser(
          { email: newEmail },
          // The API mails a confirmation link to the *new* address and keeps the
          // account on the current one until it is opened. `/auth/confirm` is the
          // page that redeems it.
          { emailRedirectTo: getAppUrl("/auth/confirm") },
        );
        if (updateError) throw updateError;

        toast({
          title: "Email Change Requested",
          description:
            "A verification email has been sent to your new address. Please check your inbox and verify the new email to complete the change.",
        });

        const {
          data: { session: newSession },
        } = await auth.getSession();
        if (newSession?.user?.email) setEmail(newSession.user.email);
        return true;
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message || "Failed to change email",
          variant: "destructive",
        });
        return false;
      } finally {
        setIsChangingEmail(false);
      }
    },
    [email, toast],
  );

  return {
    // identity
    displayName,
    setDisplayName,
    bio,
    setBio,
    avatarUrl,
    previewUrl,
    uploading,
    email,
    // profile
    saveProfile,
    uploadAvatar,
    removeAvatar,
    isUpdating,
    // credentials
    changePassword,
    changeEmail,
    isChangingPassword,
    isChangingEmail,
    // notifications
    notificationPermission,
    isRequestingPermission,
    requestPermission,
  };
}

export type ProfileSettings = ReturnType<typeof useProfileSettings>;

export default useProfileSettings;